import {useFeatureIsOn} from "@growthbook/growthbook-react";
import {t} from "@lingui/core/macro";
import * as React from "react";
import {useState} from "react";

import {addStyle} from "@khanacademy/wonder-blocks-core";
import {ActionItem, ActionMenu} from "@khanacademy/wonder-blocks-dropdown";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {MediaLayout} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import type {AppFeatures} from "@khan/analytics-growthbook/app-features";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {WRITING_COACH_CONTENT_DESCRIPTOR} from "@khan/assignments-utils";
import {useShowAssignableKhanmigoActivities} from "@khan/class-ui";
import {getPass} from "@khan/data-cookies";
import {useCoeditorContext} from "@khan/guide-coeditor-core";
import {INITIAL_WIDTH} from "@khan/guide-core";
import {doNotTranslate} from "@khan/i18n";

import CreateActivityAssignmentLauncher from "../create-activity-assignment-launcher.tsx";
import {GuideClosetLauncher} from "../guide-closet/guide-closet-launcher.tsx";
import {GuideColors} from "../guide-colors.ts";

import {HeaderType} from "./types.ts";

import dotsThreeVerticalIcon from "@phosphor-icons/core/bold/dots-three-vertical-bold.svg";
import externalLinkIcon from "@phosphor-icons/core/regular/arrow-square-out.svg";
import refreshChatIcon from "@phosphor-icons/core/regular/arrows-clockwise.svg";
import clockIcon from "@phosphor-icons/core/regular/clock.svg";
import eyeSlashIcon from "@phosphor-icons/core/regular/eye-slash.svg";
import preferencesIcon from "@phosphor-icons/core/regular/gear.svg";
import notePencilIcon from "@phosphor-icons/core/regular/note-pencil.svg";
import starIcon from "@phosphor-icons/core/regular/star.svg";

const StyledSpan = addStyle("span");

// As designed.
const MENU_BODY_MAX_WIDTH = 320;
// Dropdown position offsets
const TOP_OFFSET = 8;
const LEFT_OFFSET_DESKTOP_CHAT = 80;
const LEFT_OFFSET_DESKTOP_ACTIVITIES = 32;
const LEFT_OFFSET_AI_TOOLS = 0;
const LEFT_OFFSET_EXTENSION = 8;
const TOP_OFFSET_MOBILE_APP = -1;

const writingCoachContentID = "x1e6d165d54a37115";

interface MenuProps {
    /* Callback to reset the chat */
    onResetChat: () => unknown;
    /* Callback to open the preferences form */
    onSelectPreferences: () => unknown;
    /* Callback to toggle the menu */
    onToggleMenu: () => unknown;
    /* Optionally, signal that this menu is shown in expanded context */
    expanded?: boolean;
    /* Whether the menu is open or not */
    menuOpen: boolean;
}

interface DevMenuProps {
    onOpenThreadHistory: () => unknown;
    /* Request the current thread be downloaded. */
    onDownloadThread: () => unknown;
    /* Show additional dev-related menu items */
    showDevControls: boolean;
    /* Enable the thread history menu item (if shown) */
    threadHistoryEnabled: boolean;
}

interface ActivityProps {
    activityAssignable: boolean;
    activityContentDescriptor: string | null | undefined;
}

type DefaultMenuProps = {
    /* Used to determine menu positioning, e.g. "mobile", "desktop-chat" */
    type: HeaderType.ChromeExtension | "mobile";
} & MenuProps;

type ChatMenuProps = {
    type:
        | HeaderType.DesktopChat
        | HeaderType.SmallScreenChat
        | HeaderType.AITools;
} & MenuProps &
    DevMenuProps;

type ActivityMenuProps = {
    type: HeaderType.DesktopActivity | HeaderType.SmallScreenActivity;
} & MenuProps &
    ActivityProps;

type Props = ChatMenuProps | DefaultMenuProps | ActivityMenuProps;

/**
 * Header menu for the chat UI.  We render this full width of the parent
 * for unexpanded contexts, and constrain it to a max width for expanded.
 *
 * @param onResetChat
 * @param onSelectPreferences
 * @param expanded
 * @returns {JSX.Element}
 */
export const HeaderMenu = (props: Props): React.ReactElement => {
    const {
        expanded,
        onToggleMenu,
        onResetChat,
        onSelectPreferences,
        menuOpen,
        type,
    } = props;

    const isCoeditorOpen = useCoeditorContext().open;
    const publishAnalyticsEvent = usePublishAnalyticsEvent();
    const [showingCloset, setShowingCloset] = useState(false);
    const [showingAssignModal, setShowingAssignModal] = useState(false);
    const showFullWidthDropdown =
        type === "mobile" ||
        type === "small-screen-activity" ||
        type === "small-screen-chat";

    // Only the guide chat is eligible for the dev menu options at this time.
    const isDevOptionsEligible =
        type === "small-screen-chat" ||
        type === "desktop-chat" ||
        type === HeaderType.AITools;

    // Assign activity option is only available for activities.
    const isAssignActivityEligible =
        type === "small-screen-activity" || type === "desktop-activity";

    // For mobile, we don't want to show any opener element, so we use an empty
    // span. The opener is handled by the mobile app header, not webapp.
    const opener =
        type === "mobile" ? (
            <StyledSpan style={styles.mobileAnchor} />
        ) : (
            <IconButton
                icon={dotsThreeVerticalIcon}
                kind="tertiary"
                actionType="neutral"
                testId="khanmigo-settings-toggle"
                aria-label={
                    menuOpen ? t`Close Khanmigo menu` : t`Open Khanmigo menu`
                }
                style={[
                    styles.padButton,
                    // As designed, the "pressed" state should appear while the menu
                    // remains open.
                    menuOpen && styles.buttonActive,
                ]}
            />
        );

    const handleMenuToggle = () => {
        onToggleMenu();
    };

    // Assign Activity menu action should only be shown
    // in specific circumstances for the MVE release in March 2024.
    // TODO(CLASS-9345, alice): This will be cleaned up accordingly
    // if/when the feature is released beyond KAD Khanmigo users.
    const assignableKMActivitiesIsOn = useShowAssignableKhanmigoActivities();
    const assignWritingCoachIsOn = !!(
        useFeatureIsOn("tx-writing-coach") || getPass("tx-writing-coach")
    );
    const includeAssignActivityAction =
        isAssignActivityEligible &&
        props.activityAssignable &&
        !!props.activityContentDescriptor &&
        // TODO(CLASS-10349): Remove once `tx-writing-coach` is released 100% and
        // the feature flag is removed.
        (props.activityContentDescriptor !== WRITING_COACH_CONTENT_DESCRIPTOR ||
            assignWritingCoachIsOn) &&
        assignableKMActivitiesIsOn.status === "success" &&
        assignableKMActivitiesIsOn.data;

    const activityId =
        isAssignActivityEligible && !!props.activityContentDescriptor
            ? props.activityContentDescriptor.split(":")[1]
            : "";
    const navigateToEditScreen =
        (useFeatureIsOn<AppFeatures>("assign-from-explore-tool-cards") ||
            getPass("assign-from-explore-tool-cards")) &&
        activityId !== writingCoachContentID;

    const handleAssignActivityClick = () => {
        if (!navigateToEditScreen) {
            setShowingAssignModal(true);
        }
        publishAnalyticsEvent({
            eventType: "AiGuideActivityDropdownAssignButtonClicked",
            eventSchemaVersion: 1,
            activityId: activityId,
        });
    };

    let assignActivityAction = (
        <ActionItem
            key="assign-activity"
            testId="assign-activity-action-item"
            onClick={handleAssignActivityClick}
            label={t`Assign activity`}
            leftAccessory={
                <PhosphorIcon
                    icon={notePencilIcon}
                    size="medium"
                    color={color.offBlack}
                />
            }
            horizontalRule="full-width"
        />
    );

    if (navigateToEditScreen) {
        assignActivityAction = (
            <ActionItem
                key="assign-activity"
                testId="assign-activity-action-item"
                onClick={handleAssignActivityClick}
                href={`/teacher/edit-your-assignment/${activityId}`}
                label={t`Assign activity`}
                leftAccessory={
                    <PhosphorIcon
                        icon={notePencilIcon}
                        size="medium"
                        color={color.offBlack}
                    />
                }
                horizontalRule="full-width"
            />
        );
    }

    const defaultActions = [
        <ActionItem
            key="refresh-chat"
            testId="refresh-chat-action-item"
            onClick={onResetChat}
            label={type === "ai-tools" ? t`Restart tool` : t`Refresh chat`}
            leftAccessory={
                <PhosphorIcon
                    icon={refreshChatIcon}
                    size="medium"
                    color={color.offBlack}
                />
            }
            horizontalRule="full-width"
        />,
        <ActionItem
            key="guide-closet"
            testId="guide-closet-action-item"
            onClick={() => setShowingCloset(true)}
            label={t`Khanmigo's closet`}
            leftAccessory={
                <PhosphorIcon
                    icon={starIcon}
                    size="medium"
                    color={color.offBlack}
                />
            }
            horizontalRule="full-width"
        />,
        <ActionItem
            key="chat-history"
            testId="chat-history-action-item"
            href="/profile/me/khanmigo/history"
            label={t`Chat history`}
            leftAccessory={
                <PhosphorIcon
                    icon={clockIcon}
                    size="medium"
                    color={color.offBlack}
                />
            }
            rightAccessory={
                <PhosphorIcon icon={externalLinkIcon} color={color.blue} />
            }
            horizontalRule="full-width"
            target="_blank"
        />,
        <ActionItem
            key="preferences"
            testId="preferences-action-item"
            onClick={onSelectPreferences}
            label={t`Preferences`}
            leftAccessory={
                <PhosphorIcon
                    icon={preferencesIcon}
                    size="medium"
                    color={color.offBlack}
                />
            }
            horizontalRule={
                isDevOptionsEligible && props.showDevControls
                    ? "full-width"
                    : "none"
            }
        />,
    ];

    const maybeIncludeAssignActivityAction = includeAssignActivityAction
        ? [defaultActions[0], assignActivityAction, ...defaultActions.slice(1)]
        : defaultActions;

    const maybeDevOnlyActions =
        isDevOptionsEligible && props.showDevControls
            ? [
                  <ActionItem
                      key="chat-history-action-item"
                      testId="chat-history-action-item"
                      onClick={props.onOpenThreadHistory}
                      label={doNotTranslate`[Internal] Open conversation history`}
                      disabled={!props.threadHistoryEnabled}
                      leftAccessory={
                          <PhosphorIcon
                              icon={eyeSlashIcon}
                              size="medium"
                              color={color.offBlack}
                          />
                      }
                      horizontalRule="full-width"
                  />,
                  <ActionItem
                      key="download-thread"
                      testId="download-thread-action-item"
                      onClick={props.onDownloadThread}
                      label={doNotTranslate`[Internal] Download chat transcript`}
                      leftAccessory={
                          <PhosphorIcon
                              icon={eyeSlashIcon}
                              size="medium"
                              color={color.offBlack}
                          />
                      }
                      horizontalRule="none"
                  />,
              ]
            : [];

    const actions = [
        ...maybeIncludeAssignActivityAction,
        ...maybeDevOnlyActions,
    ];

    return (
        <MediaLayout>
            {({mediaSize}) => {
                let additionalDropdownStyle = {};
                if (showFullWidthDropdown) {
                    additionalDropdownStyle = styles.fullWidth;
                } else if (type === "desktop-chat") {
                    additionalDropdownStyle = {};
                } else if (type === "desktop-activity") {
                    additionalDropdownStyle = styles.desktopActivities;
                } else if (type === "ai-tools") {
                    additionalDropdownStyle = styles.aiTools;
                } else if (type === "chrome-extension") {
                    if (isCoeditorOpen) {
                        additionalDropdownStyle = styles.halfWidth;
                    } else {
                        if (mediaSize === "small") {
                            additionalDropdownStyle = styles.fullWidth;
                        } else {
                            additionalDropdownStyle = styles.extension;
                        }
                    }
                }

                return (
                    <>
                        <GuideClosetLauncher
                            opened={showingCloset}
                            onClose={() => {
                                setShowingCloset(false);
                            }}
                        />
                        {includeAssignActivityAction &&
                            !!props.activityContentDescriptor &&
                            showingAssignModal && (
                                <CreateActivityAssignmentLauncher
                                    activityContentDescriptor={
                                        props.activityContentDescriptor
                                    }
                                    onClose={() => setShowingAssignModal(false)}
                                    onSuccess={() => {}}
                                />
                            )}
                        <ActionMenu
                            menuText=""
                            dropdownStyle={[
                                styles.dropdown,
                                expanded && styles.expanded,
                                type === "mobile" && styles.mobile,
                                additionalDropdownStyle,
                            ]}
                            opened={menuOpen}
                            onToggle={handleMenuToggle}
                            opener={() => opener}
                        >
                            {actions}
                        </ActionMenu>
                    </>
                );
            }}
        </MediaLayout>
    );
};

const styles = {
    padButton: {
        // Takes up the outline space of the button, but doesn't add any
        // padding.
        margin: `0 ${spacing.xxxxSmall_2}px`,
    },
    buttonActive: {
        backgroundColor: color.white,
        color: GuideColors.darkPurple,
    },
    dropdown: {
        borderRadius: "unset",
        left: LEFT_OFFSET_DESKTOP_CHAT,
        width: INITIAL_WIDTH - 14,
        top: TOP_OFFSET - 4,
    },
    fullWidth: {
        width: "100vw",
        left: "unset",
    },
    halfWidth: {
        width: "50vw",
        left: LEFT_OFFSET_EXTENSION,
    },
    expanded: {
        width: MENU_BODY_MAX_WIDTH,
    },
    desktopActivities: {
        left: LEFT_OFFSET_DESKTOP_ACTIVITIES,
    },
    aiTools: {
        left: LEFT_OFFSET_AI_TOOLS,
    },
    extension: {
        left: LEFT_OFFSET_EXTENSION,
    },
    mobileAnchor: {
        // In order for the anchor to be a valid reference for the underlying
        // popper component, it must have some width and height set.
        width: 1,
        height: 1,
    },
    mobile: {
        top: TOP_OFFSET_MOBILE_APP,
    },
};
